import type { ResolvedDisplayProfile } from "./profileMatcher";

export type DisplayCssVariables = Record<`--${string}`, string>;

const BASE_WIDTH = 3840;

const BASE_DIMENSIONS = {
  "--display-header-height": 210,
  "--display-content-height": 1880,
  "--display-padding-x": 58,
  "--display-padding-bottom": 58,
  "--display-gap": 36,
  "--display-panel-padding-top": 74,
  "--display-panel-padding-x": 34,
  "--display-panel-padding-bottom": 32,
  "--display-border": 3,
  "--display-panel-border": 2,
  "--display-radius": 12,
  "--display-panel-cut": 44,
  "--display-panel-corner": 84,
  "--display-panel-offset": 12,
  "--display-panel-divider-top": 56,
  "--display-panel-divider-height": 3,
  "--display-panel-title-min-width": 430,
  "--display-panel-title-padding-y": 17,
  "--display-panel-title-padding-x": 64,
  "--display-panel-title-cut": 56,
  "--display-panel-title-mark-width": 42,
  "--display-panel-title-mark-height": 4,
  "--display-grid-size": 48,
  "--display-grid-line": 1,
  "--display-column-left": 1220,
  "--display-column-center": 1330,
  "--display-column-right": 1130,
  "--display-left-top-height": 900,
  "--display-left-bottom-height": 944,
  "--display-center-top-height": 740,
  "--display-center-bottom-height": 1104,
  "--display-right-top-height": 960,
  "--display-right-bottom-height": 884,
  "--display-title-min-width": 1630,
  "--display-title-height": 152,
  "--display-title-padding-x": 150,
  "--display-title-accent-width": 118,
  "--display-title-accent-height": 2,
  "--display-title-energy-top": 52,
  "--display-title-energy-width": 210,
  "--display-title-energy-height": 34,
  "--display-title-energy-offset": 255,
  "--display-title-accent-offset": 155,
  "--display-clock-left": 76,
  "--display-clock-top": 46,
  "--display-clock-gap": 16,
  "--display-license-gap": 36,
  "--display-license-card-gap": 18,
  "--display-license-card-height": 720,
  "--display-license-cut": 24,
  "--display-license-padding": 24,
  "--display-license-image-height": 548,
  "--display-camera-gap": 26,
  "--display-camera-cut": 20,
  "--display-camera-inset": 18,
  "--display-camera-padding-x": 32,
  "--display-camera-placeholder-size": 210,
  "--display-camera-label-padding-y": 17,
  "--display-camera-label-padding-x": 24,
  "--display-health-column": 430,
  "--display-health-height": 430,
  "--display-health-chart": 376,
  "--display-health-inner-chart": 244,
  "--display-health-orbit-offset": 20,
  "--display-health-inner-inset": 34,
  "--display-health-gap": 34,
  "--display-health-legend-gap": 22,
  "--display-health-table-offset": 452,
  "--display-health-table-row-height": 65,
  "--display-table-header-height": 74,
  "--display-table-row-height": 96,
  "--display-attendance-row-gap": 10,
  "--display-attendance-shift": 56,
  "--display-attendance-line-width": 720,
  "--display-attendance-line-height": 28,
  "--display-attendance-underlay-width": 520,
  "--display-attendance-underlay-offset": 12,
  "--display-report-gap": 28,
  "--display-report-padding": 20,
  "--display-report-card-gap": 14,
  "--display-report-paper-padding": 18,
  "--display-legend-icon": 22,
  "--display-legend-gap": 20,
  "--display-legend-min-height": 52,
  "--display-legend-padding-y": 6,
  "--display-legend-padding-x": 15,
  "--display-avatar-size": 52,
  "--display-avatar-gap": 14,
  "--display-pill-min-width": 78,
  "--display-pill-padding-y": 5,
  "--display-pill-padding-x": 10,
  "--display-report-cut": 18,
  "--display-report-badge-offset": 20,
  "--display-report-badge-icon": 28,
  "--display-report-badge-gap": 10,
  "--display-report-badge-padding-y": 8,
  "--display-report-badge-padding-x": 16,
  "--font-clock": 40,
  "--font-title": 92,
  "--font-panel": 44,
  "--font-attendance-label": 54,
  "--font-attendance-number": 336,
  "--font-camera": 32,
  "--font-license": 40,
  "--font-license-subtitle": 25,
  "--font-placeholder": 34,
  "--font-report": 36,
  "--font-report-badge": 29,
  "--font-health-total": 104,
  "--font-health-label": 31,
  "--font-health-unit": 48,
  "--font-table": 33,
  "--font-report-paper": 28,
  "--font-body": 26,
  "--font-small": 21
} as const;

const MINIMUM_PHYSICAL_FONT_SIZES: Partial<Record<keyof typeof BASE_DIMENSIONS, number>> = {
  "--font-table": 15,
  "--font-camera": 14,
  "--font-body": 12,
  "--font-small": 10
};

export function createDisplayProfileVariables(resolved: ResolvedDisplayProfile): DisplayCssVariables {
  const scale = resolved.stage.width / BASE_WIDTH;
  const variables: DisplayCssVariables = {
    "--display-stage-width": `${resolved.stage.width}px`,
    "--display-stage-height": `${resolved.stage.height}px`,
    "--display-layout-density": scale.toFixed(4)
  };

  for (const [name, value] of Object.entries(BASE_DIMENSIONS)) {
    const scaled = value * scale;
    const minimum = (MINIMUM_PHYSICAL_FONT_SIZES[name as keyof typeof BASE_DIMENSIONS] ?? 0) / resolved.renderPixelRatio;
    variables[name as `--${string}`] = `${formatPixels(Math.max(scaled, minimum))}px`;
  }

  return variables;
}

function formatPixels(value: number): string {
  return String(Number(value.toFixed(3)));
}
